import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// prevent double-mount if script is loaded twice or on soft navigations
if (typeof window !== "undefined") {
  if (window.__FAQ_MOUNTED__) {
    // already initialized, do nothing
  } else {
    window.__FAQ_MOUNTED__ = true;

    (function faqMount() {
      const onReady = (fn) =>
        window.wp && wp.domReady
          ? wp.domReady(fn)
          : document.addEventListener("DOMContentLoaded", fn);

      function setState(item, open, animate = true) {
        const head = item.querySelector(".faq-item__head");
        const panel = item.querySelector(".faq-item__panel");
        const vLine = item.querySelector(".faq-item__icon .icon-v");
        if (!head || !panel) return;

        item.dataset.open = open ? "1" : "0";
        head.setAttribute("aria-expanded", String(open));

        if (vLine)
          gsap.to(vLine, {
            opacity: open ? 0 : 1,
            duration: animate ? 0.25 : 0,
          });

        if (open) {
          panel.hidden = false;
          const h = panel.scrollHeight;
          gsap.fromTo(
            panel,
            { height: 0, opacity: 0 },
            {
              height: h,
              opacity: 1,
              duration: animate ? 0.32 : 0,
              ease: "power2.out",
              onComplete: () => (panel.style.height = "auto"),
            }
          );
        } else {
          const h = panel.offsetHeight || panel.scrollHeight || 0;
          gsap.fromTo(
            panel,
            { height: h, opacity: 1 },
            {
              height: 0,
              opacity: 0,
              duration: animate ? 0.24 : 0,
              ease: "power2.in",
              onComplete: () => {
                panel.hidden = true;
                panel.style.height = ""; // cleanup
              },
            }
          );
        }
      }

      function bindList(list) {
        // guard: don't bind twice to the same list
        if (list.__bound) return;
        list.__bound = true;

        const items = Array.from(list.querySelectorAll(".faq-item"));
        if (!items.length) return;

        const openFirst = list.getAttribute("data-open-first") === "1";

        // init closed
        items.forEach((it) => {
          const head = it.querySelector(".faq-item__head");
          const panel = it.querySelector(".faq-item__panel");
          const vLine = it.querySelector(".faq-item__icon .icon-v");
          if (head) head.setAttribute("aria-expanded", "false");
          if (panel) {
            panel.hidden = true;
            panel.style.height = ""; // ensure clean
          }
          if (vLine) vLine.style.opacity = "1";
          it.dataset.open = "0";
        });

        if (openFirst && items[0]) {
          setState(items[0], true, false);
        }

        // event delegation to avoid per-item double listeners
        list.addEventListener("click", (e) => {
          const head = e.target.closest(".faq-item__head");
          if (!head || !list.contains(head)) return;

          e.preventDefault(); // safegaurd against <a> inside head
          e.stopPropagation(); // avoid bubbling to other handlers

          const item = head.closest(".faq-item");
          if (!item) return;

          const willOpen = item.dataset.open !== "1";

          // close others
          items.forEach(
            (it) =>
              it !== item && it.dataset.open === "1" && setState(it, false)
          );

          // toggle this one
          setState(item, willOpen);
        });

        // row reveal on scroll
        gsap.set(items, { y: 18, opacity: 0 });
        gsap.to(items, {
          y: 0,
          opacity: 1,
          duration: 0.45,
          ease: "power2.out",
          stagger: 0.08,
          scrollTrigger: { trigger: list, start: "top 80%", once: true },
        });
      }

      function animateLead(section) {
        const lead = section.querySelector(".faq__lead");
        if (!lead) return;

        const eyebrow = lead.querySelector(".faq__eyebrow");
        const dot = eyebrow?.querySelector(".dot");
        const title = lead.querySelector(".faq__headline");
        const intro = lead.querySelector(".faq__intro");

        gsap.set([eyebrow, title, intro], { opacity: 0, y: 18 });
        if (dot) gsap.set(dot, { scale: 0.6 });

        const tl = gsap.timeline({
          defaults: { ease: "power3.out" },
          scrollTrigger: { trigger: section, start: "top 80%", once: true },
        });

        if (eyebrow) tl.to(eyebrow, { opacity: 1, y: 0, duration: 0.4 });
        if (dot)
          tl.to(dot, { scale: 1, duration: 0.35, ease: "back.out(2)" }, "<");
        if (title) tl.to(title, { opacity: 1, y: 0, duration: 0.6 }, "-=0.1");
        if (intro) tl.to(intro, { opacity: 1, y: 0, duration: 0.5 }, "-=0.2");
      }

      onReady(() => {
        document.querySelectorAll(".faq").forEach(animateLead);
        document.querySelectorAll(".faq__list").forEach(bindList);

        // Editor re-bind (idempotent thanks to __bound guard)
        const root = document.querySelector(".editor-styles-wrapper");
        if (root) {
          const mo = new MutationObserver(() => {
            document
              .querySelectorAll(".faq")
              .forEach((sec) => animateLead(sec));
            document.querySelectorAll(".faq__list").forEach(bindList);
          });
          mo.observe(root, { childList: true, subtree: true });
        }
      });
    })();
  }
}
